// SGL-403: the Visibility Manager's Google Business Profile card has two ways in.
//
//   - the welcome-screen starter card, which brings its own question, and
//   - the owner typing the request: the model calls review_google_business_profile,
//     a tool that fetches nothing and only tells the browser to open the same card
//     once the reply has finished.
//
// One flow, two entry points. This file holds the two small decisions the browser
// makes on that signal; the card itself is built in NewChatWindow.

/** The chat-service tool (tool_registry.py). Matched by name in the `tool_call` event. */
export const GBP_REVIEW_TOOL = "review_google_business_profile";

/** What the starter card asks on the owner's behalf. Also the thread's first user turn. */
export const GBP_STARTER_QUESTION = "What's holding back my Google Business Profile?";

/**
 * True for the `tool_call` SSE event of the Business Profile review tool when it opened
 * the card. The tool also answers plain questions about the profile ("how many reviews
 * do I have?") and refuses the card for a profile it could not read; neither opens it.
 * `status: "shown"` is the earlier, signal-only tool's result, accepted so the card still
 * opens while that backend is live.
 */
export function isGbpReviewToolCall(data: unknown): boolean {
  if (typeof data !== "object" || data === null) return false;
  const { tool_name, result } = data as { tool_name?: unknown; result?: unknown };
  if (tool_name !== GBP_REVIEW_TOOL) return false;
  let parsed: unknown = result;
  if (typeof result === "string") {
    try {
      parsed = JSON.parse(result);
    } catch {
      return false;
    }
  }
  if (typeof parsed !== "object" || parsed === null) return false;
  const { fix_card, status } = parsed as { fix_card?: unknown; status?: unknown };
  return fix_card === "opened" || (fix_card === undefined && status === "shown");
}

/** How the card came to be, which decides where and how it is stored. */
export type GbpCardOrigin = { kind: "starter" } | { kind: "typed"; threadId: string | null };

/** What POST /visibility/gbp-proposal needs: the question to record, and the thread to
 *  put the card on (null = whichever thread is open, or a new one). */
export interface GbpSaveTarget {
  question: string | null;
  threadId: string | null;
}

/**
 * A card the owner typed for lands on the thread that already holds their message, and
 * records no question: a second, invented user turn would sit beside the real one. The
 * starter's card brings its own. A typed card whose thread is unknown also brings the
 * starter question, because a new thread needs a first message to be titled from.
 */
export function gbpSaveTarget(origin: GbpCardOrigin): GbpSaveTarget {
  if (origin.kind === "typed" && origin.threadId) {
    return { question: null, threadId: origin.threadId };
  }
  return { question: GBP_STARTER_QUESTION, threadId: null };
}
